/**
 * Configuration — Support Tickets
 *
 * Controls how the six fixed Support Ticket Types are made available to
 * Visitors, Customers and Businesses. Does not store operational tickets,
 * messages, internal notes or resolution records.
 */

import { formatConfigListDate } from './tradeCategoryConfig';

export { formatConfigListDate };

export const SUPPORT_TICKET_CONFIG_ROUTE = '/admin/configuration/support-ticket';

export const SUPPORT_TICKET_MODULE = 'Support Tickets';

export const TICKET_TYPES = {
  GENERAL_ENQUIRY: 'General Enquiry',
  ACCOUNT_ISSUE: 'Account Issue',
  BILLING_SUBSCRIPTIONS: 'Billing & Subscriptions',
  TECHNICAL_ISSUE: 'Technical Issue',
  FEEDBACK_SUGGESTIONS: 'Feedback and Suggestions',
  OTHER: 'Other',
};

export const TICKET_TYPE_OPTIONS = [
  TICKET_TYPES.GENERAL_ENQUIRY,
  TICKET_TYPES.ACCOUNT_ISSUE,
  TICKET_TYPES.BILLING_SUBSCRIPTIONS,
  TICKET_TYPES.TECHNICAL_ISSUE,
  TICKET_TYPES.FEEDBACK_SUGGESTIONS,
  TICKET_TYPES.OTHER,
];

export const PRIORITY = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

export const PRIORITY_OPTIONS = [
  { value: PRIORITY.LOW, label: 'Low' },
  { value: PRIORITY.MEDIUM, label: 'Medium' },
  { value: PRIORITY.HIGH, label: 'High' },
];

export const DEFAULT_PRIORITY_BY_TICKET_TYPE = {
  [TICKET_TYPES.GENERAL_ENQUIRY]: PRIORITY.LOW,
  [TICKET_TYPES.ACCOUNT_ISSUE]: PRIORITY.MEDIUM,
  [TICKET_TYPES.BILLING_SUBSCRIPTIONS]: PRIORITY.MEDIUM,
  [TICKET_TYPES.TECHNICAL_ISSUE]: PRIORITY.HIGH,
  [TICKET_TYPES.FEEDBACK_SUGGESTIONS]: PRIORITY.LOW,
  [TICKET_TYPES.OTHER]: PRIORITY.MEDIUM,
};

export const CONFIG_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export const CONFIG_STATUS_OPTIONS = [
  { value: CONFIG_STATUS.ACTIVE, label: 'Active' },
  { value: CONFIG_STATUS.INACTIVE, label: 'Inactive' },
];

export const AVAILABILITY = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
};

/** Shared public entry point — Contact Us (covers the public contact / help flow). */
export const ENTRY_POINT = {
  HELP_CENTRE_CONTACT_US: 'Contact Us',
  CUSTOMER_DASHBOARD: 'Customer Dashboard',
  BUSINESS_DASHBOARD: 'Business Dashboard',
};

export const VISITOR_ENTRY_POINT = ENTRY_POINT.HELP_CENTRE_CONTACT_US;

export const CUSTOMER_ENTRY_POINT_OPTIONS = [
  { value: ENTRY_POINT.HELP_CENTRE_CONTACT_US, label: 'Contact Us' },
  { value: ENTRY_POINT.CUSTOMER_DASHBOARD, label: 'Customer Dashboard' },
];

export const BUSINESS_ENTRY_POINT_OPTIONS = [
  { value: ENTRY_POINT.HELP_CENTRE_CONTACT_US, label: 'Contact Us' },
  { value: ENTRY_POINT.BUSINESS_DASHBOARD, label: 'Business Dashboard' },
];

export const COMPLIANCE_ASK_MORE_INFO_NOTE =
  'Used for Compliance Ask More Info. New linked tickets use this Rule’s Priority. User submission settings do not disable that request path.';

export const AVAILABILITY_SCOPE_NOTE =
  'Availability and entry points apply to users starting their own tickets.';

export const CONFIG_LIST_ACTION_LABELS = {
  view: 'View',
  edit: 'Edit',
};

export const MESSAGES = {
  saved: 'Support Ticket configuration saved successfully.',
  saveFailed: 'Configuration could not be saved. Review the highlighted fields and try again.',
  noChange: 'No changes to save.',
  empty: 'No Support Ticket configurations match your filters.',
  notFound: 'Support Ticket configuration could not be found.',
  stale: 'This Rule has changed. Reload and review the latest values before saving.',
};

export const VALIDATION_MESSAGES = {
  ruleUnavailable: 'This Rule is unavailable. Reload the configuration list.',
  identityImmutable: 'The Rule’s ID, Module and Ticket Type cannot be changed.',
  duplicateTicketType: 'Only one Rule is allowed for each Ticket Type.',
  duplicateId: 'This configuration ID is already in use.',
  availabilityMissing: 'Select Enabled or Disabled.',
  visitorEntryPointFixed: 'Visitor Entry Point is fixed as Contact Us.',
  customerEntryPointsInvalid: 'Select only Contact Us or Customer Dashboard.',
  customerEntryPointsRequired: 'Select at least one Customer entry point.',
  businessEntryPointsInvalid: 'Select only Contact Us or Business Dashboard.',
  businessEntryPointsRequired: 'Select at least one Business entry point.',
  repeatedEntryPoint: 'Select each entry point only once.',
  submitterRequired: 'Enable at least one submitter type or select Inactive.',
  priorityRequired: 'Select Low, Medium or High.',
  priorityInvalid: 'Select Low, Medium or High.',
  statusRequired: 'Select Active or Inactive.',
  statusInvalid: 'Select Active or Inactive.',
  additionalConditions: 'Additional Conditions are not supported here.',
  complianceMappingImmutable: 'The Compliance request mapping cannot be changed here.',
  stale: 'This Rule has changed. Reload and review the latest values before saving.',
};

export function formatSupportTicketConfigDate(value) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
}

export function getAvailabilityLabel(enabled) {
  return enabled ? AVAILABILITY.ENABLED : AVAILABILITY.DISABLED;
}

/** Screen 1 Available For — Inactive Rules show None. */
export function getAvailableForSummary(config) {
  if (config?.status === CONFIG_STATUS.INACTIVE) return 'None';
  const parts = [];
  if (config?.visitorAvailable) parts.push('Visitor');
  if (config?.customerAvailable) parts.push('Customer');
  if (config?.businessAvailable) parts.push('Business');
  return parts.length ? parts.join(', ') : 'None';
}

/** Screen 1 Entry Points — Inactive Rules show —. */
export function getEntryPointsSummary(config) {
  if (config?.status === CONFIG_STATUS.INACTIVE) return '—';
  const points = [];
  if (config?.visitorAvailable) points.push(VISITOR_ENTRY_POINT);
  if (config?.customerAvailable) {
    (config.customerEntryPoints || []).forEach((point) => {
      if (!points.includes(point)) points.push(point);
    });
  }
  if (config?.businessAvailable) {
    (config.businessEntryPoints || []).forEach((point) => {
      if (!points.includes(point)) points.push(point);
    });
  }
  return points.length ? points.join('; ') : '—';
}

export function getListActionsForConfiguration() {
  return ['view', 'edit'];
}

export function findSupportTicketConfiguration(configurations, configId) {
  const id = decodeURIComponent(String(configId || '')).trim();
  if (!id) return null;
  return (configurations || []).find((row) => row.id === id) || null;
}

function legacyEntryPointMap(value) {
  if (
    value === 'Help Centre' ||
    value === 'Contact Us' ||
    value === 'Help Centre / Contact Us'
  ) {
    return ENTRY_POINT.HELP_CENTRE_CONTACT_US;
  }
  return value;
}

function normalizeEntryPoints(values, allowed) {
  const allowedSet = new Set(allowed);
  const mapped = (values || []).map(legacyEntryPointMap);
  return [...new Set(mapped.filter((value) => allowedSet.has(value)))];
}

export function normalizeSupportTicketConfiguration(config = {}) {
  const ticketType = config.ticketType || '';
  const customerAllowed = CUSTOMER_ENTRY_POINT_OPTIONS.map((o) => o.value);
  const businessAllowed = BUSINESS_ENTRY_POINT_OPTIONS.map((o) => o.value);
  return {
    id: config.id || '',
    module: SUPPORT_TICKET_MODULE,
    ticketType,
    visitorAvailable: Boolean(config.visitorAvailable),
    customerAvailable: Boolean(config.customerAvailable),
    businessAvailable: Boolean(config.businessAvailable),
    visitorEntryPoint: VISITOR_ENTRY_POINT,
    customerEntryPoints: normalizeEntryPoints(config.customerEntryPoints, customerAllowed),
    businessEntryPoints: normalizeEntryPoints(config.businessEntryPoints, businessAllowed),
    automaticPriority:
      config.automaticPriority ||
      DEFAULT_PRIORITY_BY_TICKET_TYPE[ticketType] ||
      PRIORITY.MEDIUM,
    status: config.status === CONFIG_STATUS.INACTIVE ? CONFIG_STATUS.INACTIVE : CONFIG_STATUS.ACTIVE,
    dateAdded: config.dateAdded || '',
    lastUpdated: config.lastUpdated || '',
    lastUpdatedBy: config.lastUpdatedBy || '',
  };
}

function hasDuplicateEntryPoints(values) {
  const mapped = (values || []).map(legacyEntryPointMap);
  return mapped.length !== new Set(mapped).size;
}

/**
 * Validate a proposed Rule. Pass baseline + allConfigurations for identity / uniqueness / stale checks.
 */
export function collectSupportTicketConfigValidationErrors(
  draft,
  { baseline = null, allConfigurations = [], openedLastUpdated = null } = {},
) {
  const errors = {};
  const priorityValues = PRIORITY_OPTIONS.map((o) => o.value);
  const statusValues = CONFIG_STATUS_OPTIONS.map((o) => o.value);
  const customerAllowed = CUSTOMER_ENTRY_POINT_OPTIONS.map((o) => o.value);
  const businessAllowed = BUSINESS_ENTRY_POINT_OPTIONS.map((o) => o.value);

  if (!draft?.id || !baseline) {
    errors.form = VALIDATION_MESSAGES.ruleUnavailable;
    return errors;
  }

  if (
    draft.id !== baseline.id ||
    draft.module !== SUPPORT_TICKET_MODULE ||
    draft.ticketType !== baseline.ticketType
  ) {
    errors.identity = VALIDATION_MESSAGES.identityImmutable;
  }

  const others = (allConfigurations || []).filter((row) => row.id !== baseline.id);
  if (others.some((row) => row.id === draft.id)) {
    errors.id = VALIDATION_MESSAGES.duplicateId;
  }
  if (others.some((row) => row.ticketType === draft.ticketType)) {
    errors.ticketType = VALIDATION_MESSAGES.duplicateTicketType;
  }

  if (
    openedLastUpdated &&
    baseline.lastUpdated &&
    String(openedLastUpdated) !== String(baseline.lastUpdated)
  ) {
    errors.stale = VALIDATION_MESSAGES.stale;
  }

  if (typeof draft.visitorAvailable !== 'boolean') {
    errors.visitorAvailable = VALIDATION_MESSAGES.availabilityMissing;
  }
  if (typeof draft.customerAvailable !== 'boolean') {
    errors.customerAvailable = VALIDATION_MESSAGES.availabilityMissing;
  }
  if (typeof draft.businessAvailable !== 'boolean') {
    errors.businessAvailable = VALIDATION_MESSAGES.availabilityMissing;
  }

  if (draft.visitorEntryPoint && draft.visitorEntryPoint !== VISITOR_ENTRY_POINT) {
    errors.visitorEntryPoint = VALIDATION_MESSAGES.visitorEntryPointFixed;
  }

  const rawCustomer = (draft.customerEntryPoints || []).map(legacyEntryPointMap);
  const rawBusiness = (draft.businessEntryPoints || []).map(legacyEntryPointMap);
  const customerPoints = normalizeEntryPoints(draft.customerEntryPoints, customerAllowed);
  const businessPoints = normalizeEntryPoints(draft.businessEntryPoints, businessAllowed);

  if (hasDuplicateEntryPoints(draft.customerEntryPoints)) {
    errors.customerEntryPoints = VALIDATION_MESSAGES.repeatedEntryPoint;
  } else if (rawCustomer.some((p) => !customerAllowed.includes(p))) {
    errors.customerEntryPoints = VALIDATION_MESSAGES.customerEntryPointsInvalid;
  } else if (draft.customerAvailable && customerPoints.length === 0) {
    errors.customerEntryPoints = VALIDATION_MESSAGES.customerEntryPointsRequired;
  }

  if (hasDuplicateEntryPoints(draft.businessEntryPoints)) {
    errors.businessEntryPoints = VALIDATION_MESSAGES.repeatedEntryPoint;
  } else if (rawBusiness.some((p) => !businessAllowed.includes(p))) {
    errors.businessEntryPoints = VALIDATION_MESSAGES.businessEntryPointsInvalid;
  } else if (draft.businessAvailable && businessPoints.length === 0) {
    errors.businessEntryPoints = VALIDATION_MESSAGES.businessEntryPointsRequired;
  }

  if (!draft.automaticPriority) {
    errors.automaticPriority = VALIDATION_MESSAGES.priorityRequired;
  } else if (!priorityValues.includes(draft.automaticPriority)) {
    errors.automaticPriority = VALIDATION_MESSAGES.priorityInvalid;
  }

  if (!draft.status) {
    errors.status = VALIDATION_MESSAGES.statusRequired;
  } else if (!statusValues.includes(draft.status)) {
    errors.status = VALIDATION_MESSAGES.statusInvalid;
  }

  if (
    draft.status === CONFIG_STATUS.ACTIVE &&
    !draft.visitorAvailable &&
    !draft.customerAvailable &&
    !draft.businessAvailable
  ) {
    errors.submitterAvailability = VALIDATION_MESSAGES.submitterRequired;
  }

  if (Array.isArray(draft.conditions) && draft.conditions.length > 0) {
    errors.conditions = VALIDATION_MESSAGES.additionalConditions;
  }

  // Fixed Compliance Ask More Info mapping (STC-001 / General Enquiry) is display-only.
  if (
    (baseline.id === 'STC-001' || baseline.ticketType === TICKET_TYPES.GENERAL_ENQUIRY) &&
    (draft.id !== 'STC-001' || draft.ticketType !== TICKET_TYPES.GENERAL_ENQUIRY)
  ) {
    errors.complianceMapping = VALIDATION_MESSAGES.complianceMappingImmutable;
  }

  return errors;
}

export function supportTicketConfigHasChanges(before, after) {
  if (!before || !after) return true;
  if (Boolean(before.visitorAvailable) !== Boolean(after.visitorAvailable)) return true;
  if (Boolean(before.customerAvailable) !== Boolean(after.customerAvailable)) return true;
  if (Boolean(before.businessAvailable) !== Boolean(after.businessAvailable)) return true;
  if (before.automaticPriority !== after.automaticPriority) return true;
  if (before.status !== after.status) return true;
  if (
    (before.customerEntryPoints || []).join('|') !== (after.customerEntryPoints || []).join('|')
  ) {
    return true;
  }
  if (
    (before.businessEntryPoints || []).join('|') !== (after.businessEntryPoints || []).join('|')
  ) {
    return true;
  }
  return false;
}

export function buildChangeSummary(before, after) {
  const changes = [];
  if (!before || !after) return changes;

  if (Boolean(before.visitorAvailable) !== Boolean(after.visitorAvailable)) {
    changes.push(
      `Visitor Availability: ${getAvailabilityLabel(before.visitorAvailable)} → ${getAvailabilityLabel(after.visitorAvailable)}`,
    );
  }
  if (Boolean(before.customerAvailable) !== Boolean(after.customerAvailable)) {
    changes.push(
      `Customer Availability: ${getAvailabilityLabel(before.customerAvailable)} → ${getAvailabilityLabel(after.customerAvailable)}`,
    );
  }
  if (
    (before.customerEntryPoints || []).join('|') !== (after.customerEntryPoints || []).join('|')
  ) {
    changes.push(
      `Customer Entry Points: ${(before.customerEntryPoints || []).join('; ') || '—'} → ${(after.customerEntryPoints || []).join('; ') || '—'}`,
    );
  }
  if (Boolean(before.businessAvailable) !== Boolean(after.businessAvailable)) {
    changes.push(
      `Business Availability: ${getAvailabilityLabel(before.businessAvailable)} → ${getAvailabilityLabel(after.businessAvailable)}`,
    );
  }
  if (
    (before.businessEntryPoints || []).join('|') !== (after.businessEntryPoints || []).join('|')
  ) {
    changes.push(
      `Business Entry Points: ${(before.businessEntryPoints || []).join('; ') || '—'} → ${(after.businessEntryPoints || []).join('; ') || '—'}`,
    );
  }
  if (before.automaticPriority !== after.automaticPriority) {
    changes.push(
      `Priority: ${before.automaticPriority || '—'} → ${after.automaticPriority || '—'}`,
    );
  }
  if (before.status !== after.status) {
    changes.push(`Configuration Status: ${before.status || '—'} → ${after.status || '—'}`);
  }
  return changes;
}

function buildFixedConfiguration({ id, ticketType, dateAdded, lastUpdated }) {
  return normalizeSupportTicketConfiguration({
    id,
    ticketType,
    visitorAvailable: true,
    customerAvailable: true,
    businessAvailable: true,
    customerEntryPoints: [
      ENTRY_POINT.HELP_CENTRE_CONTACT_US,
      ENTRY_POINT.CUSTOMER_DASHBOARD,
    ],
    businessEntryPoints: [
      ENTRY_POINT.HELP_CENTRE_CONTACT_US,
      ENTRY_POINT.BUSINESS_DASHBOARD,
    ],
    automaticPriority: DEFAULT_PRIORITY_BY_TICKET_TYPE[ticketType],
    status: CONFIG_STATUS.ACTIVE,
    dateAdded,
    lastUpdated,
    lastUpdatedBy: 'System',
  });
}

/** Exactly six fixed configuration records — cannot be added or deleted. */
export function buildSampleSupportTicketConfigurations() {
  const dateAdded = '2026-08-01T00:00:00.000Z';
  const lastUpdated = '2026-08-08T00:00:00.000Z';

  return [
    buildFixedConfiguration({
      id: 'STC-001',
      ticketType: TICKET_TYPES.GENERAL_ENQUIRY,
      dateAdded,
      lastUpdated,
    }),
    buildFixedConfiguration({
      id: 'STC-002',
      ticketType: TICKET_TYPES.ACCOUNT_ISSUE,
      dateAdded,
      lastUpdated,
    }),
    buildFixedConfiguration({
      id: 'STC-003',
      ticketType: TICKET_TYPES.BILLING_SUBSCRIPTIONS,
      dateAdded,
      lastUpdated,
    }),
    buildFixedConfiguration({
      id: 'STC-004',
      ticketType: TICKET_TYPES.TECHNICAL_ISSUE,
      dateAdded,
      lastUpdated,
    }),
    buildFixedConfiguration({
      id: 'STC-005',
      ticketType: TICKET_TYPES.FEEDBACK_SUGGESTIONS,
      dateAdded,
      lastUpdated,
    }),
    buildFixedConfiguration({
      id: 'STC-006',
      ticketType: TICKET_TYPES.OTHER,
      dateAdded,
      lastUpdated,
    }),
  ];
}
